// La navigation du produit : le rail mène aux modules, la bande d'onglets
// au contenu de la portée affichée, la flèche revient au portefeuille. La
// coquille s'en sert ; les pages n'en portent rien.
//
// Une portée par méthode : « fichiers » existe des deux côtés et ne mène pas
// au même écran, alors le choix se fait au typage plutôt qu'à l'exécution.
//
// La navigation connaît le CABINET : sous `/w/:wId`, chaque module et chaque
// onglet mène à sa route produit (tous sont branchés depuis le lot D6). Hors
// d'un cabinet, rien ne mène nulle part : la coquille n'y est plus montée, et
// la navigation reste sur place plutôt que d'inventer une adresse.

import type {
  OngletCabinet,
  OngletDossier,
} from "@app/components/compta/ui/BandeOnglets";
import type { ComptaModule } from "@app/components/compta/ui/ComptaRail";
import { AuthContext } from "@app/lib/auth/AuthContext";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  routeProduitDossier,
  routeProduitModule,
  routeProduitOngletCabinet,
  routeProduitPortefeuille,
} from "./routes-compta";

interface NavigationCompta {
  allerAuModule: (module: ComptaModule) => void;
  allerAOngletCabinet: (onglet: OngletCabinet) => void;
  /** Changer d'onglet dans le dossier affiché, celui que l'adresse porte. */
  allerAOngletDossier: (onglet: OngletDossier) => void;
  /** Ouvrir un dossier précis, par l'espace qui le porte. */
  allerAuDossier: (spaceId: string, onglet: OngletDossier) => void;
  revenirAuPortefeuille: () => void;
  /** Ce vers quoi la navigation mène vraiment : le rail et la bande grisent le reste. */
  disponible: {
    module: (module: ComptaModule) => boolean;
    ongletCabinet: (onglet: OngletCabinet) => boolean;
    ongletDossier: (onglet: OngletDossier) => boolean;
  };
}

/**
 * Le cabinet courant, ou null hors du fournisseur d'authentification.
 *
 * `useContext` plutôt que `useWorkspace()` : ce dernier lève hors du
 * fournisseur, et une coquille ne doit jamais tomber pour cela.
 */
function useCabinetOptionnel(): { sId: string } | null {
  const contexte = useContext(AuthContext);
  return contexte?.workspace ?? null;
}

// Un espace quelconque, pour savoir si un onglet de dossier a une route
// produit quand l'adresse n'en porte pas (la table ne dépend pas de l'espace).
const ESPACE_TEMOIN = "_";

export function useNavigationCompta(): NavigationCompta {
  const naviguer = useNavigate();
  const cabinet = useCabinetOptionnel();
  // L'espace du dossier affiché, lu dans l'adresse (`spaces/:spaceId`) : la
  // bande d'onglets n'a pas à le connaître, et hors d'un dossier il n'y en a
  // pas.
  const { spaceId } = useParams<{ spaceId: string }>();

  const aller = (adresse: string | null) => {
    if (adresse !== null) {
      naviguer(adresse);
    }
  };

  const versModule = (module: ComptaModule) =>
    cabinet ? routeProduitModule(cabinet.sId, module) : null;
  const versOngletCabinet = (onglet: OngletCabinet) =>
    cabinet ? routeProduitOngletCabinet(cabinet.sId, onglet) : null;
  const versOngletDossier = (onglet: OngletDossier, espace?: string) =>
    cabinet && espace ? routeProduitDossier(cabinet.sId, espace, onglet) : null;

  return {
    allerAuModule: (module) => aller(versModule(module)),
    allerAOngletCabinet: (onglet) => aller(versOngletCabinet(onglet)),
    allerAOngletDossier: (onglet) => aller(versOngletDossier(onglet, spaceId)),
    allerAuDossier: (espace, onglet) =>
      aller(versOngletDossier(onglet, espace)),
    disponible: {
      module: (module) => versModule(module) !== null,
      ongletCabinet: (onglet) => versOngletCabinet(onglet) !== null,
      ongletDossier: (onglet) =>
        versOngletDossier(onglet, spaceId ?? ESPACE_TEMOIN) !== null,
    },
    revenirAuPortefeuille: () =>
      aller(cabinet ? routeProduitPortefeuille(cabinet.sId) : null),
  };
}
